import {
  GetRelatedTransactionsDetailSuccessResponseInfo,
  RelatedTransaction,
  RelatedTransactionForecast,
  RelatedTransactionSalesResult,
} from '@/coreInfrastructureModule/axios/generated/inventoryManage';
import {
  IRelatedTransactionsDetail,
  IRelatedTransaction,
  IRelatedTransactionForecast,
  IRelatedTransactionSalesResult,
} from '../domains/entities/relatedTransaction';

/**
 * APIレスポンスの型をUI用のドメイン型に変換する
 */
export class RelatedTransactionsAdapter {
  /**
   * RelatedTransactionSalesResultをIRelatedTransactionSalesResultに変換する
   * @param salesResult - APIレスポンスの販売実績情報
   */
  private static adaptSalesResult(salesResult: RelatedTransactionSalesResult): IRelatedTransactionSalesResult {
    return {
      targetYear: salesResult.targetYear,
      targetMonth: salesResult.targetMonth,
      amount: salesResult.amount,
    };
  }

  /**
   * RelatedTransactionForecastをIRelatedTransactionForecastに変換する
   * @param forecast - APIレスポンスの内示情報
   */
  private static adaptForecast(forecast: RelatedTransactionForecast): IRelatedTransactionForecast {
    return {
      receivedYear: forecast.receivedYear,
      receivedMonth: forecast.receivedMonth,
      targetYear: forecast.targetYear,
      targetMonth: forecast.targetMonth,
      amount: forecast.amount,
    };
  }

  /**
   * RelatedTransactionをIRelatedTransactionに変換する
   * @param transaction - APIレスポンスの関連取引情報
   */
  private static adaptTransaction(transaction: RelatedTransaction): IRelatedTransaction {
    return {
      baseBusinessId: transaction.baseBusinessId,
      customerNumber: transaction.customerNumber,
      customerName: transaction.customerName,
      salesDestinationNumber: transaction.salesDestinationNumber,
      salesDestinationName: transaction.salesDestinationName,
      salesResults: transaction.salesResults.map((sr) => this.adaptSalesResult(sr)),
      customerForecasts: transaction.customerForecasts.map((cf) => this.adaptForecast(cf)),
    };
  }

  /**
   * GetRelatedTransactionsDetailSuccessResponseInfoをIRelatedTransactionsDetailに変換する
   * @param response - APIレスポンス
   * @param startYearMonth - 開始年月
   * @param endYearMonth - 終了年月
   */
  public static adapt(
    response: GetRelatedTransactionsDetailSuccessResponseInfo,
    startYearMonth: { year: number; month: number },
    endYearMonth: { year: number; month: number }
  ): IRelatedTransactionsDetail {
    // 月別カラムリストを生成（開始年月から終了年月まで）
    const monthlyColumns = this.generateMonthlyColumns(startYearMonth, endYearMonth);

    return {
      transactions: response.transactions.map((transaction) => this.adaptTransaction(transaction)),
      monthlyColumns,
    };
  }

  /**
   * 開始年月から終了年月までの月別カラムリストを生成する
   * @param startYearMonth - 開始年月
   * @param endYearMonth - 終了年月
   */
  private static generateMonthlyColumns(
    startYearMonth: { year: number; month: number },
    endYearMonth: { year: number; month: number }
  ): { year: number; month: number }[] {
    const columns: { year: number; month: number }[] = [];
    let currentYear = startYearMonth.year;
    let currentMonth = startYearMonth.month;

    while (currentYear < endYearMonth.year || (currentYear === endYearMonth.year && currentMonth <= endYearMonth.month)) {
      columns.push({ year: currentYear, month: currentMonth });
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    return columns;
  }
}
